import {updateMonitorSetting, updateDisableEffectsSetting, stopAudio, isTunerActive, restartTunerWithNewDevice} from './tuner.js';

class SettingsManager {
    constructor() {
        this.elements = this.getDOMElements();
        this.bufferSizeMap = this.createBufferSizeMap();
        this.defaultSettings = this.getDefaultSettings();
        this.currentSettings = { ...this.defaultSettings };
    }

    getDOMElements() {
        return {
            bufferSizeSlider: document.getElementById('bufferSize'),
            bufferValue: document.getElementById('bufferValue'),
            sensitivitySlider: document.getElementById('sensitivity'),
            sensitivityValue: document.getElementById('sensitivityValue'),
            tuningSelect: document.getElementById('tuning'),
            microphoneSelect: document.getElementById('microphoneSelect'),
            refreshMicrophonesBtn: document.getElementById('refreshMicrophones'),
            disableEffectsCheckbox: document.getElementById('disableEffects')
        };
    }

    createBufferSizeMap() {
        return {
            10: 2048,  11: 4096,  12: 8192,
            13: 16384, 14: 32768
        };
    }

    getDefaultSettings() {
        return {
            bufferSize: 4096,
            sensitivity: 0.4,
            tuning: 'standard',
            stringNotes: ['E4', 'B3', 'G3', 'D3', 'A2', 'E2'],
            monitorMicrophone: false,
            monitorVolume: 0.5,
            microphoneDeviceId: null,
            disableEffects: false
        };
    }

    async init() {
        this.loadSettings();
        this.updateSettingsUI();
        this.setupEventListeners();
        await this.updateMicrophoneList();
    }

    setupEventListeners() {
        const {
            bufferSizeSlider, sensitivitySlider, microphoneSelect, refreshMicrophonesBtn, disableEffectsCheckbox
        } = this.elements;

        bufferSizeSlider.addEventListener('input', (e) => this.handleBufferSizeChange(e));
        sensitivitySlider.addEventListener('input', (e) => this.handleSensitivityChange(e));
        microphoneSelect.addEventListener('change', (e) => this.handleMicrophoneChange(e));
        refreshMicrophonesBtn.addEventListener('click', () => this.updateMicrophoneList());
        if (disableEffectsCheckbox) {
            disableEffectsCheckbox.addEventListener('change', (e) => this.handleDisableEffectsChange(e));
        }
    }

    getCurrentSettings() {
        return { ...this.currentSettings };
    }

    loadSettings() {
        try {
            const savedSettings = localStorage.getItem('guitarTunerSettings');
            if (savedSettings) {
                const parsed = JSON.parse(savedSettings);
                this.currentSettings = { ...this.defaultSettings, ...parsed };
            }
        } catch (error) {
            console.error('Ошибка загрузки настроек:', error);
            this.currentSettings = { ...this.defaultSettings };
        }
    }

    saveSettings() {
        try {
            localStorage.setItem('guitarTunerSettings', JSON.stringify(this.currentSettings));
        } catch (error) {
            console.error('Ошибка сохранения настроек:', error);
        }
    }

    updateSettingsUI() {
        const {
            bufferSizeSlider, bufferValue, sensitivitySlider,
            sensitivityValue, microphoneSelect, disableEffectsCheckbox
        } = this.elements;

        // Размер буфера
        bufferSizeSlider.value = Object.keys(this.bufferSizeMap).find(
            key => this.bufferSizeMap[key] === this.currentSettings.bufferSize
        ) || '11';
        bufferValue.textContent = this.currentSettings.bufferSize;

        // Чувствительность
        sensitivitySlider.value = this.currentSettings.sensitivity;
        sensitivityValue.textContent = this.currentSettings.sensitivity.toFixed(2);

        // Микрофон - значение будет установлено после обновления списка
        if (this.currentSettings.microphoneDeviceId) {
            microphoneSelect.value = this.currentSettings.microphoneDeviceId;
        }

        // Отключить эффекты
        if (disableEffectsCheckbox) {
            disableEffectsCheckbox.checked = this.currentSettings.disableEffects || false;
        }
    }

    handleBufferSizeChange(event) {
        const sizeValue = parseInt(event.target.value);
        this.currentSettings.bufferSize = this.bufferSizeMap[sizeValue];
        this.elements.bufferValue.textContent = this.currentSettings.bufferSize;
        this.saveSettings();
    }

    handleSensitivityChange(event) {
        this.currentSettings.sensitivity = parseFloat(event.target.value);
        this.elements.sensitivityValue.textContent = this.currentSettings.sensitivity.toFixed(2);
        this.saveSettings();
    }

    handleTuningChange(event) {
        this.currentSettings.tuning = event.target.value;
        this.updateStringNotes();
        this.saveSettings();
    }

    updateStringNotes() {
        const tuningPresets = {
            standard: ['E2', 'A2', 'D3', 'G3', 'B3', 'E4'],
            dropd: ['D2', 'A2', 'D3', 'G3', 'B3', 'E4'],
            openg: ['D2', 'G2', 'D3', 'G3', 'B3', 'D4'],
            dadgad: ['D2', 'A2', 'D3', 'G3', 'A3', 'D4']
        };

        this.currentSettings.stringNotes = tuningPresets[this.currentSettings.tuning] || tuningPresets.standard;
    }

    handleMonitorChange(event) {
        this.currentSettings.monitorMicrophone = event.target.checked;
        this.saveSettings();
        this.updateMonitorFromSettings();
    }

    handleMonitorVolumeChange(event) {
        this.currentSettings.monitorVolume = parseFloat(event.target.value);
        this.elements.monitorVolumeValue.textContent =
            `${Math.round(this.currentSettings.monitorVolume * 100)}%`;
        this.saveSettings();
        this.updateMonitorFromSettings();
    }

    updateMonitorFromSettings() {
        if (typeof updateMonitorSetting === 'function') {
            updateMonitorSetting(this.currentSettings.monitorMicrophone);
        }
    }

    handleDisableEffectsChange(event) {
        this.currentSettings.disableEffects = event.target.checked;
        this.saveSettings();
        this.updateDisableEffectsFromSettings();
    }

    updateDisableEffectsFromSettings() {
        if (typeof updateDisableEffectsSetting === 'function') {
            updateDisableEffectsSetting(this.currentSettings.disableEffects);
        }
    }

    async updateMicrophoneList() {
        try {
            // Проверяем поддержку MediaDevices API
            if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
                console.warn('MediaDevices API не поддерживается');
                return;
            }

            // Останавливаем текущий поток микрофона перед обновлением списка
            // Это предотвращает конфликты при перечислении устройств
            try {
                stopAudio();
            } catch (e) {
                // Игнорируем ошибку, если микрофон не был активен
            }

            // Запрашиваем разрешение на доступ к микрофону, если ещё не было
            // Это нужно для получения полного списка устройств (с названиями)
            try {
                const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                // Сразу останавливаем временный поток
                tempStream.getTracks().forEach(track => track.stop());
            } catch (e) {
                // Игнорируем ошибку, если пользователь не дал разрешение
                console.warn('Разрешение на микрофон не получено, список может быть неполным');
            }

            // Получаем список всех аудио-устройств
            const devices = await navigator.mediaDevices.enumerateDevices();
            const audioInputs = devices.filter(device => device.kind === 'audioinput');

            // Убираем дубликаты по deviceId (приоритет устройству с названием)
            const uniqueDevices = new Map();
            audioInputs.forEach(device => {
                const existingDevice = uniqueDevices.get(device.deviceId);
                // Если устройства с таким deviceId еще нет, добавляем его
                if (!existingDevice) {
                    uniqueDevices.set(device.deviceId, device);
                } 
                // Если у текущего устройства есть label, а у сохраненного нет - заменяем
                else if (device.label && !existingDevice.label) {
                    uniqueDevices.set(device.deviceId, device);
                }
            });
            const finalDevices = Array.from(uniqueDevices.values());

            const { microphoneSelect } = this.elements;
            
            // Сохраняем текущее выбранное значение
            const currentValue = microphoneSelect.value;

            // Очищаем список (кроме опции "По умолчанию")
            microphoneSelect.innerHTML = '<option value="">По умолчанию</option>';

            // Добавляем найденные микрофоны (без дубликатов)
            finalDevices.forEach(device => {
                const option = document.createElement('option');
                option.value = device.deviceId;
                option.textContent = device.label || `Микрофон ${microphoneSelect.options.length}`;
                microphoneSelect.appendChild(option);
            });

            // Восстанавливаем выбранное значение, если оно всё ещё существует
            if (currentValue) {
                const optionExists = Array.from(microphoneSelect.options).some(
                    opt => opt.value === currentValue
                );
                if (optionExists) {
                    microphoneSelect.value = currentValue;
                } else {
                    // Если выбранного устройства больше нет, сохраняем ID, но используем "По умолчанию"
                    microphoneSelect.value = '';
                }
            }
        } catch (error) {
            console.error('Ошибка при обновлении списка микрофонов:', error);
        }
    }

    async handleMicrophoneChange(event) {
        const deviceId = event.target.value || null;
        this.currentSettings.microphoneDeviceId = deviceId;
        this.saveSettings();
        
        // Если микрофон активен, перезапускаем его с новым устройством
        if (isTunerActive()) {
            try {
                await restartTunerWithNewDevice();
                console.log('Микрофон перезапущен с новым устройством');
            } catch (error) {
                console.error('Ошибка при перезапуске микрофона:', error);
                alert(`Ошибка при перезапуске микрофона: ${error.message}\n\nПопробуйте остановить и включить микрофон вручную.`);
            }
        }
    }

    getMicrophoneDeviceId() {
        return this.currentSettings.microphoneDeviceId || null;
    }
}

// Создаем экземпляр и экспортируем функции для обратной совместимости
const settingsManager = new SettingsManager();

export const initSettings = async () => {
    await settingsManager.init();
};

export const getCurrentSettings = () => {
    return settingsManager.getCurrentSettings();
};

export const getMicrophoneDeviceId = () => {
    return settingsManager.getMicrophoneDeviceId();
};